import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { AuthContext } from "../App";
import { message, notification } from "antd";
import { getToken, getKeycloakConfig, isAuthenticated, keycloakLogin } from "../auth";
import { colors as BRAND } from "../theme";
import { cardStyle, btnPrimary, btnSecondary } from "../styles";
import { setupMobileViewport } from "../utils/viewport";
import { formatBytes } from "../utils/format";
import { apiCall } from "../utils/api";

// TSP-121: Manage Pictures - generic attachment surface for any Etendo table.
// Canonical route is /#/managepictures; /#/upload is kept as a back-compat alias.
// URL contract:
//   /#/managepictures?tableId=<ad_table_id>&recordId=<ad_record_id>
//            &label=<friendly>          optional - document type (e.g. "Work Order")
//            &returnUrl=<encoded URL>   optional - link back to the calling page
//                                       (typically the Mobile WO screen). Fully
//                                       URL-encoded; rendered as a clickable link
//                                       on the document number/searchkey.
//
// The same page serves Work Orders today and can be reused for Inspections,
// Containers, Sales Orders, etc. by just changing tableId.

setupMobileViewport();

const API_URL = window.location.origin;
const DESCRIPTION_MAX = 255;

// Responsive breakpoints. Below TABLET_BREAKPOINT we render a phone layout
// (3 columns, 480px max). Above we widen the page so tablets and desktops
// don't show a narrow center sliver of thumbnails.
const TABLET_BREAKPOINT = 600;
const DESKTOP_BREAKPOINT = 960;
function pickLayout(width) {
  if (width >= DESKTOP_BREAKPOINT) return { maxWidth: 960, columns: 5 };
  if (width >= TABLET_BREAKPOINT) return { maxWidth: 760, columns: 4 };
  return { maxWidth: 480, columns: 3 };
}

function parseHashParams() {
  const q = window.location.hash.split("?")[1] || "";
  return new URLSearchParams(q);
}

// Icons
const IconCamera = ({ size = 20, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
const IconImages = ({ size = 20, color = BRAND.text }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);
const IconTrash = ({ size = 16, color = BRAND.error }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);
const IconCheck = ({ size = 16, color = BRAND.success }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconPlay = ({ size = 36, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="rgba(0,0,0,0.45)" strokeWidth="1" strokeLinejoin="round">
    <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.45)" stroke="none" />
    <polygon points="10,8 17,12 10,16" fill={color} stroke="none" />
  </svg>
);
const IconClose = ({ size = 22, color = BRAND.text }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="6" y1="18" x2="18" y2="6" />
  </svg>
);

// File-extension based heuristic - the c_file table doesn't expose a MIME
// column, and the list endpoint returns just the filename. We treat anything
// outside the obvious image set as "video" for grid rendering purposes; the
// thumb endpoint will return 415 for actual video content.
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|3gp|3gpp|webm|mkv|avi|qt)$/i;
function isVideoName(name) {
  return !!name && VIDEO_EXT_RE.test(name);
}

// Thumbnails + full-size images authenticate via ?t=<jwt> since <img> can't
// set headers. Token is short-lived (24h) and passed over HTTPS only.
function authSrc(url) {
  const t = getToken();
  const sep = url.indexOf("?") === -1 ? "?" : "&";
  return t ? `${url}${sep}t=${encodeURIComponent(t)}` : url;
}

function ExistingGrid({ items, columns, onOpenAttachment, onDelete, onEditDescription, selectMode, selectedIds, onToggleSelect, readOnly }) {
  if (items.length === 0) {
    return (
      <div style={{ ...cardStyle, textAlign: "center", padding: "32px 14px", color: BRAND.textMuted }}>
        <IconImages size={36} color={BRAND.textMuted} />
        <div style={{ marginTop: 10, fontSize: 14 }}>No pictures uploaded yet</div>
        <div style={{ marginTop: 4, fontSize: 12 }}>Take a photo or video, or pick from gallery to add the first one.</div>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns || 3}, 1fr)`, gap: 8, marginBottom: 12 }}>
      {items.map((it) => {
        const isVideo = isVideoName(it.name);
        const isSelected = selectMode && selectedIds && selectedIds.has(it.id);
        const handleDeleteClick = (e) => {
          e.stopPropagation();
          e.preventDefault();
          if (window.confirm(`Delete "${it.description || it.name}"?`)) {
            onDelete(it.id);
          }
        };
        const handleTileClick = (e) => {
          // Don't trigger when the tap landed on the inline delete button or
          // the description editor.
          if (e && e.defaultPrevented) return;
          if (selectMode) {
            onToggleSelect(it.id);
            return;
          }
          onOpenAttachment(it);
        };
        const handleDescriptionClick = (e) => {
          e.stopPropagation();
          e.preventDefault();
          if (selectMode) {
            // While selecting, tapping the description still toggles the tile.
            onToggleSelect(it.id);
            return;
          }
          // Anonymous read-only view (TSP-121): description is not editable.
          if (readOnly) return;
          onEditDescription(it);
        };
        return (
          <div
            key={it.id}
            style={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <div
              onClick={handleTileClick}
              style={{
                position: "relative",
                paddingTop: "100%",
                borderRadius: 8,
                overflow: "hidden",
                cursor: "pointer",
                background: "#000",
                border: isSelected ? `2px solid ${BRAND.primary}` : `1px solid ${BRAND.border}`,
                outline: isSelected ? `2px solid ${BRAND.primary}` : "none",
                outlineOffset: isSelected ? -2 : 0,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <ThumbImage id={it.id} alt={it.description || it.name} isVideo={isVideo} />
              {/* Play-icon overlay for videos. Sits above the thumbnail/icon,
                  below the delete button. */}
              {isVideo && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <IconPlay size={36} color="#fff" />
                </div>
              )}
              {/* Selection checkmark overlay (select mode only). */}
              {isSelected && (
                <div
                  style={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: BRAND.primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                    zIndex: 2,
                    pointerEvents: "none",
                  }}
                >
                  <IconCheck size={16} color="#fff" />
                </div>
              )}
              {/* Inline delete button - hidden in select mode for cleaner
                  UX, and hidden entirely for anonymous read-only viewers. */}
              {!selectMode && !readOnly && (
                <button
                  onClick={handleDeleteClick}
                  aria-label="Delete picture"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    border: "none",
                    fontSize: 14,
                    lineHeight: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    WebkitTapHighlightColor: "transparent",
                    zIndex: 2,
                  }}
                >
                  {"×"}
                </button>
              )}
            </div>
            {/* Description below the tile. Tap to edit. Two-line clamp with
                ellipsis. Empty state renders a faint italic placeholder so
                users know they can add text. maxHeight is locked to exactly
                two line-heights so a partial third line cannot leak past the
                ellipsis (-webkit-line-clamp on its own does not enforce
                height when the parent allows it to grow). */}
            <div
              onClick={handleDescriptionClick}
              role={readOnly ? undefined : "button"}
              aria-label={readOnly ? undefined : (it.description ? "Edit description" : "Add description")}
              style={{
                marginTop: 4,
                height: "2.6em",
                maxHeight: "2.6em",
                padding: "4px 2px",
                fontSize: 11,
                lineHeight: 1.3,
                color: BRAND.textMuted,
                fontStyle: it.description ? "normal" : "italic",
                opacity: it.description ? 1 : 0.7,
                cursor: readOnly ? "default" : "pointer",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                wordBreak: "break-word",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {/* Anonymous viewers don't see the "Add description" prompt -
                  the empty div is kept only to hold the grid row height. */}
              {it.description || (readOnly ? "" : "Add description")}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Modal-style overlay for inline-editing a single attachment's description.
// Built with plain divs + cardStyle so the page doesn't pull in antd Modal.
function DescriptionEditor({ item, onSave, onCancel }) {
  const [draft, setDraft] = useState(item.description || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(item.id, draft.trim().substring(0, DESCRIPTION_MAX));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          ...cardStyle,
          width: "100%",
          maxWidth: 380,
          padding: 16,
          background: BRAND.pageBg || "#fff",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.text, marginBottom: 8 }}>
          Edit description
        </div>
        <div style={{ fontSize: 12, color: BRAND.textMuted, marginBottom: 8, wordBreak: "break-word" }}>
          {item.name}
        </div>
        <textarea
          autoFocus
          maxLength={DESCRIPTION_MAX}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Heel, Manhole, etc."
          rows={3}
          style={{
            width: "100%",
            boxSizing: "border-box",
            fontSize: 14,
            padding: "8px 10px",
            border: `1px solid ${BRAND.border}`,
            borderRadius: 6,
            outline: "none",
            resize: "vertical",
            fontFamily: "inherit",
          }}
        />
        <div style={{ fontSize: 11, color: BRAND.textMuted, marginTop: 4, textAlign: "right" }}>
          {draft.length} / {DESCRIPTION_MAX}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{ ...btnSecondary, flex: 1, minHeight: 40 }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ ...btnPrimary, flex: 1, minHeight: 40 }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Full-size image viewer modal. Shown when the user taps an image tile in
// the grid. Backdrop tap or X button closes. Videos still open in a new tab
// (handled by openAttachment) so the device's native player takes over.
// When the item has a description, render it as a caption below the image
// (multi-line aware via whiteSpace pre-wrap) so users can read the full
// text instead of being limited to the two-line ellipsis on the tile.
function ImageViewer({ item, hasPrev, hasNext, onPrev, onNext, onClose }) {
  const hasDescription = !!(item.description && item.description.trim().length > 0);
  // Keyboard support - left/right flip through pictures, Esc closes.
  // capture:true so we fire before any other handler that might consume
  // arrow keys, e.g. the description's overflow scroll (TSP-121 QA fix).
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft" && hasPrev) {
        e.preventDefault();
        e.stopPropagation();
        onPrev();
      } else if (e.key === "ArrowRight" && hasNext) {
        e.preventDefault();
        e.stopPropagation();
        onNext();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [hasPrev, hasNext, onPrev, onNext, onClose]);

  const navBtnStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    border: "none",
    fontSize: 28,
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitTapHighlightColor: "transparent",
    zIndex: 2,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.92)",
        zIndex: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: "fixed",
          top: "calc(12px + env(safe-area-inset-top, 0px))",
          right: 12,
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          border: "none",
          fontSize: 22,
          lineHeight: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          WebkitTapHighlightColor: "transparent",
          zIndex: 201,
        }}
      >
        {"×"}
      </button>
      {/* Image + nav arrows wrapped together. Arrows sit inside the image
          bounds (position:absolute relative to this container) so they
          never overlap the description below - earlier they were fixed
          to viewport center which caused them to land on top of the
          description, where clicks/keys hit the wrong target. */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          maxWidth: "100%",
          minHeight: 0,
          flex: "0 1 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={authSrc(`${API_URL}/api/upload/file/${item.id}`)}
          alt={item.description || item.name}
          draggable={false}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
        {hasPrev && (
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            aria-label="Previous picture"
            style={{ ...navBtnStyle, left: 8 }}
          >
            {"‹"}
          </button>
        )}
        {hasNext && (
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            aria-label="Next picture"
            style={{ ...navBtnStyle, right: 8 }}
          >
            {"›"}
          </button>
        )}
      </div>
      {hasDescription && (
        <div
          onClick={(e) => e.stopPropagation()}
          // tabIndex -1 so a stray click on the description can't move
          // keyboard focus into a scroll container that would eat arrow
          // keys before our window handler sees them.
          tabIndex={-1}
          style={{
            marginTop: 12,
            padding: "10px 16px",
            color: "#fff",
            fontSize: 14,
            lineHeight: 1.4,
            maxWidth: 600,
            maxHeight: "30vh",
            overflowY: "auto",
            textAlign: "center",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            background: "rgba(0,0,0,0.45)",
            borderRadius: 6,
            flex: "0 0 auto",
            outline: "none",
          }}
        >
          {item.description}
        </div>
      )}
    </div>
  );
}

// Per-tile thumbnail. For videos (or any file the thumb endpoint can't
// generate, e.g. backend returns 415), show a generic film-strip icon on a
// dark backdrop so users still see something instead of a broken-image glyph.
function ThumbImage({ id, alt, isVideo }) {
  const [errored, setErrored] = useState(false);
  if (isVideo || errored) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "#1f2937",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Simple film-strip glyph; play overlay is drawn on top by the parent. */}
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
          <line x1="6" y1="4" x2="6" y2="20" />
          <line x1="18" y1="4" x2="18" y2="20" />
          <line x1="2" y1="12" x2="22" y2="12" />
        </svg>
      </div>
    );
  }
  return (
    <img
      src={authSrc(`${API_URL}/api/upload/thumb/${id}`)}
      alt={alt}
      loading="lazy"
      onError={() => setErrored(true)}
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
}

function QueueItem({ item, onRemove, onChangeDescription }) {
  const isDone = item.status === "done";
  const isError = item.status === "error";
  const isUploading = item.status === "uploading";
  const isPending = item.status === "pending";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.description || "");
  const locked = isUploading || isDone;

  const save = () => {
    const trimmed = draft.trim().substring(0, DESCRIPTION_MAX);
    onChangeDescription(item.id, trimmed);
    setEditing(false);
  };

  return (
    <div style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: `1px solid ${BRAND.border}`, alignItems: "flex-start" }}>
      <div style={{ width: 48, height: 48, borderRadius: 6, background: "#000", flexShrink: 0, overflow: "hidden", position: "relative", marginTop: 2 }}>
        {item.previewUrl && <img src={item.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
        {isDone && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(5,150,105,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <IconCheck size={22} color="#fff" />
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: BRAND.text, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11, color: BRAND.textMuted, marginTop: 2 }}>
          {formatBytes(item.size)}
          {isError && <span style={{ color: BRAND.error, marginLeft: 8 }}>Failed{item.error ? ` - ${item.error}` : ""}</span>}
          {isDone && <span style={{ color: BRAND.success, marginLeft: 8 }}>Uploaded</span>}
          {isPending && <span style={{ marginLeft: 8 }}>Waiting</span>}
        </div>
        {isUploading && (
          <div style={{ marginTop: 6, height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${item.progress}%`, background: BRAND.primary, transition: "width 0.2s" }} />
          </div>
        )}
        {/* Description editor (optional, only shown before done) */}
        {!isDone && !isError && !editing && (
          <div
            onClick={() => !locked && setEditing(true)}
            style={{
              marginTop: 6,
              fontSize: 12,
              color: item.description ? BRAND.text : BRAND.primary,
              cursor: locked ? "default" : "pointer",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              opacity: locked ? 0.7 : 1,
            }}
          >
            {item.description ? `"${item.description}"` : "+ Add description (optional)"}
          </div>
        )}
        {!isDone && !isError && editing && (
          <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
            <input
              type="text"
              autoFocus
              maxLength={DESCRIPTION_MAX}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(item.description || ""); setEditing(false); } }}
              placeholder="e.g. Heel, Manhole, etc."
              style={{
                flex: 1,
                fontSize: 12,
                padding: "6px 8px",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 6,
                outline: "none",
              }}
            />
          </div>
        )}
      </div>
      {!isUploading && (
        <button
          onClick={() => onRemove(item.id)}
          style={{ background: "transparent", border: "none", padding: 8, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}
          aria-label="Remove"
        >
          <IconTrash />
        </button>
      )}
    </div>
  );
}

// Compute the Etendo base URL for the downloadAttachments servlet. We prefer
// the value the auth service gave us in /auth/keycloak/config, but the config
// is loaded asynchronously, so we also derive a same-origin fallback so the
// thumbnails are tappable on first paint.
function deriveEtendoBaseUrlFallback() {
  const host = window.location.host;
  if (host.includes("dev.tanksoftpro.com")) {
    return "https://dev.tanksoftpro.com/erp_dev";
  }
  if (host.includes("tv.tanksoftpro.com") || host.includes("kagpilot.tanksoftpro.com")) {
    return "https://kagpilot.tanksoftpro.com/erp_uat";
  }
  // Last-resort fallback: assume same origin with /erp_uat. The KC config
  // override usually lands within ~50ms of mount so users rarely see this.
  return window.location.origin + "/erp_uat";
}

export default function UploadPage() {
  const user = useContext(AuthContext);
  const params = parseHashParams();
  const tableId = params.get("tableId") || "";
  const recordId = params.get("recordId") || "";
  const contextLabel = params.get("label") || "";
  // returnUrl is fully URL-encoded by the caller (Etendo). Decode lazily so
  // an empty/missing value collapses to "" and we can render plain text.
  const returnUrlRaw = params.get("returnUrl") || "";
  const returnUrl = useMemo(() => {
    if (!returnUrlRaw) return "";
    try { return decodeURIComponent(returnUrlRaw); } catch (_) { return returnUrlRaw; }
  }, [returnUrlRaw]);
  // closeOnExit=true tells handleClose to attempt window.close() only and not
  // fall back to returnUrl. Set by the FlexDoc ManagePictures decorator so that
  // links opened from PDFs in a fresh tab don't bounce the user back to the
  // calling Etendo page when they hit the X to dismiss.
  const closeOnExit = params.get("closeOnExit") === "true";

  // TSP-121: anonymous read-only mode. When there is no TankView session the
  // page still renders so a Wash Certificate PDF recipient (e.g. a Shipper
  // Supervisor with no ad_user login) can tap through and view the pictures.
  // Every write action - upload, delete, edit description, the document
  // back-link - is suppressed; a Sign In affordance unlocks the full page.
  const readOnly = !isAuthenticated();

  // One-time toast telling anonymous viewers they're in read-only mode and
  // how to unlock the rest. Use a bottom-right notification so the Sign In
  // button in the top-left stays visible (John's TSP-121 QA feedback).
  useEffect(() => {
    if (!readOnly) return;
    notification.info({
      message: "Read-only access",
      description: "Tap Sign In (top left) to unlock upload and edit features.",
      placement: "bottomRight",
      duration: 3,
      key: "tsp121-readonly-info",
    });
  }, [readOnly]);

  const [existing, setExisting] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [queue, setQueue] = useState([]);
  const [etendoBaseUrl, setEtendoBaseUrl] = useState(deriveEtendoBaseUrlFallback);
  // Track viewport width so phones get a 3-column 480px layout while tablets
  // and desktops use a wider page with more thumbnail columns. Resize handler
  // covers tablet rotation.
  const [layout, setLayout] = useState(() => pickLayout(window.innerWidth));
  useEffect(() => {
    const onResize = () => setLayout(pickLayout(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Inline-edit overlay: holds the item being edited, or null when closed.
  const [editingItem, setEditingItem] = useState(null);
  // Image viewer modal: holds the id of the image being viewed, or null when
  // closed. Tracked as an id (not the full object) so the viewer can flip
  // through siblings in the current sort order without going stale when the
  // underlying `existing` list updates. Videos bypass this and open in a new
  // tab via openAttachment.
  const [viewingId, setViewingId] = useState(null);
  // Sort order for the existing-pictures grid (TSP-121 QA feedback). Default
  // is "newest first" by upload date; users can switch to oldest-first or
  // sort by description.
  const [sortKey, setSortKey] = useState("created-desc");
  // Multi-select state. selectMode toggles the alternate UI; selectedIds is
  // the working set, kept as a Set for O(1) membership checks. bulkDraft is
  // the textarea value in the sticky bottom bar; bulkSaving guards against
  // double-submits.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDraft, setBulkDraft] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Pull etendoBaseUrl from the KC config endpoint. The backend agent is
  // adding this field; if absent we keep the fallback derived above.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getKeycloakConfig();
        if (cancelled) return;
        if (cfg && cfg.etendoBaseUrl) setEtendoBaseUrl(cfg.etendoBaseUrl);
      } catch (_) { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ref mirror of `queue` so uploadOne can always read the latest item without
  // relying on setState updater timing (TSP-121 fix - the previous snapshot
  // trick was returning early under batched React updates).
  const queueRef = useRef([]);
  queueRef.current = queue;

  const canUse = !!(tableId && recordId);

  const fetchExisting = useCallback(async () => {
    if (!canUse) { setLoadingExisting(false); return; }
    try {
      // Anonymous viewers send no Authorization header; the list endpoint
      // accepts that (tvAuthOptional) and returns the same read-only data.
      const token = getToken();
      const res = await fetch(`${API_URL}/api/upload/list?tableId=${encodeURIComponent(tableId)}&recordId=${encodeURIComponent(recordId)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to load attachments");
      const data = await res.json();
      setExisting(data.attachments || []);
    } catch (err) {
      message.error(err.message || "Failed to load attachments");
    } finally {
      setLoadingExisting(false);
    }
  }, [tableId, recordId, canUse]);

  useEffect(() => { fetchExisting(); }, [fetchExisting]);

  // Upload one queued item with real XHR progress, then move it into `existing`.
  const uploadOne = useCallback((itemId) => {
    // Read from the ref so we get the latest queue regardless of batched
    // updates. The previous snapshot trick (using setQueue as a getter) was
    // returning early in production builds.
    const snap = queueRef.current.find((q) => q.id === itemId);
    if (!snap || !snap.file) return;

    setQueue((qs) => qs.map((q) => (q.id === itemId ? { ...q, status: "uploading", progress: 0 } : q)));

    const form = new FormData();
    form.append("file", snap.file, snap.name);
    form.append("tableId", tableId);
    form.append("recordId", recordId);
    if (snap.description) form.append("description", snap.description);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/api/upload/file`);
    xhr.setRequestHeader("Authorization", `Bearer ${getToken()}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        setQueue((qs) => qs.map((q) => (q.id === itemId ? { ...q, progress: pct } : q)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        let resp = {};
        try { resp = JSON.parse(xhr.responseText); } catch (_) {}
        setQueue((qs) => qs.map((q) => (q.id === itemId ? { ...q, status: "done", progress: 100 } : q)));
        setExisting((ex) => [
          {
            id: resp.id,
            name: resp.name || snap.name,
            description: resp.description || "",
            size: resp.size || snap.size,
            created: resp.created || new Date().toISOString(),
            createdByName: user?.displayName || user?.username || "You",
          },
          ...ex,
        ]);
        setTimeout(() => {
          setQueue((qs) => qs.filter((q) => q.id !== itemId));
        }, 800);
      } else {
        let msg = "Upload failed";
        try { msg = JSON.parse(xhr.responseText).message || msg; } catch (_) {}
        setQueue((qs) => qs.map((q) => (q.id === itemId ? { ...q, status: "error", error: msg } : q)));
      }
    };
    xhr.onerror = () => {
      setQueue((qs) => qs.map((q) => (q.id === itemId ? { ...q, status: "error", error: "Network error" } : q)));
    };
    xhr.send(form);
  }, [tableId, recordId, user]);

  // Kick off uploads sequentially (pendings → uploading).
  useEffect(() => {
    const inFlight = queue.some((q) => q.status === "uploading");
    if (inFlight) return;
    const next = queue.find((q) => q.status === "pending");
    if (next) uploadOne(next.id);
  }, [queue, uploadOne]);

  const addFiles = useCallback((fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (!canUse) {
      message.warning("Missing tableId / recordId in URL - can't upload");
      return;
    }
    const newItems = Array.from(fileList).map((f) => ({
      id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: f.name || `photo-${Date.now()}.jpg`,
      size: f.size,
      file: f,
      previewUrl: URL.createObjectURL(f),
      description: "",
      status: "pending",
      progress: 0,
    }));
    setQueue((qs) => [...qs, ...newItems]);
    message.success(`${newItems.length} file${newItems.length > 1 ? "s" : ""} queued`);
  }, [canUse]);

  const removeQueueItem = (id) =>
    setQueue((qs) => qs.filter((q) => q.id !== id));

  const setQueueDescription = (id, description) =>
    setQueue((qs) => qs.map((q) => (q.id === id ? { ...q, description } : q)));

  // Sorted view of `existing`. sortKey is user-selectable in the header:
  //   created-desc   newest upload first (default)
  //   created-asc    oldest upload first
  //   description    A->Z by description, empties last, created DESC as
  //                  the stable tiebreaker
  const sortedExisting = useMemo(() => {
    const cmpCreatedDesc = (a, b) => {
      const ta = a.created ? new Date(a.created).getTime() : 0;
      const tb = b.created ? new Date(b.created).getTime() : 0;
      return tb - ta;
    };
    const cmpCreatedAsc = (a, b) => -cmpCreatedDesc(a, b);
    const cmpDescription = (a, b) => {
      const da = (a.description || "").trim();
      const db = (b.description || "").trim();
      if (da === "" && db === "") return cmpCreatedDesc(a, b);
      if (da === "") return 1;
      if (db === "") return -1;
      const c = da.toLowerCase().localeCompare(db.toLowerCase());
      return c !== 0 ? c : cmpCreatedDesc(a, b);
    };
    const cmp =
      sortKey === "created-asc"
        ? cmpCreatedAsc
        : sortKey === "description"
        ? cmpDescription
        : cmpCreatedDesc;
    return existing.slice().sort(cmp);
  }, [existing, sortKey]);

  // Items the in-page lightbox can actually show. Videos open in a new tab
  // via openAttachment, so they're excluded from the prev/next sequence.
  const viewableItems = useMemo(
    () => sortedExisting.filter((it) => !isVideoName(it.name)),
    [sortedExisting]
  );
  const viewingIndex = useMemo(
    () => (viewingId ? viewableItems.findIndex((it) => it.id === viewingId) : -1),
    [viewingId, viewableItems]
  );
  const viewingItem = viewingIndex >= 0 ? viewableItems[viewingIndex] : null;
  const showPrev = useCallback(() => {
    if (viewingIndex > 0) setViewingId(viewableItems[viewingIndex - 1].id);
  }, [viewingIndex, viewableItems]);
  const showNext = useCallback(() => {
    if (viewingIndex >= 0 && viewingIndex < viewableItems.length - 1) {
      setViewingId(viewableItems[viewingIndex + 1].id);
    }
  }, [viewingIndex, viewableItems]);

  // Save a single description (used by the inline edit overlay).
  const saveDescription = useCallback(async (id, description) => {
    try {
      await apiCall(`/api/upload/${id}/description`, {
        method: "PATCH",
        body: JSON.stringify({ description }),
      });
      setExisting((ex) => ex.map((e) => (e.id === id ? { ...e, description } : e)));
      message.success("Description updated");
      setEditingItem(null);
    } catch (err) {
      message.error(err.message || "Failed to update description");
      // Keep the editor open on error so the user can retry.
    }
  }, []);

  const enterSelectMode = () => {
    setSelectMode(true);
    setSelectedIds(new Set());
    setBulkDraft("");
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkDraft("");
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Apply the bulkDraft description to every selected id. Promise.all is fine
  // since typical batches are 5-20 items. We surface a single success toast
  // (or a single error toast naming the failure count) and update local state
  // for whichever PATCHes succeeded.
  const applyBulkDescription = async () => {
    if (bulkSaving) return;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const description = bulkDraft.trim().substring(0, DESCRIPTION_MAX);
    setBulkSaving(true);
    const results = await Promise.all(ids.map(async (id) => {
      try {
        await apiCall(`/api/upload/${id}/description`, {
          method: "PATCH",
          body: JSON.stringify({ description }),
        });
        return { id, ok: true };
      } catch (err) {
        return { id, ok: false };
      }
    }));
    const okIds = results.filter((r) => r.ok).map((r) => r.id);
    const failCount = results.length - okIds.length;
    if (okIds.length > 0) {
      setExisting((ex) => ex.map((e) => (okIds.includes(e.id) ? { ...e, description } : e)));
    }
    if (failCount === 0) {
      message.success(`Applied to ${okIds.length} ${okIds.length === 1 ? "picture" : "pictures"}`);
      exitSelectMode();
    } else if (okIds.length === 0) {
      message.error(`Failed to update ${failCount} ${failCount === 1 ? "picture" : "pictures"}`);
    } else {
      message.error(`Updated ${okIds.length}, failed ${failCount}`);
      exitSelectMode();
    }
    setBulkSaving(false);
  };

  const deleteExisting = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/upload/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Delete failed");
      }
      setExisting((ex) => ex.filter((e) => e.id !== id));
      message.success("Picture deleted");
    } catch (err) {
      message.error(err.message || "Delete failed");
    }
  };

  const handleCamera = () => cameraInputRef.current && cameraInputRef.current.click();
  const handleGallery = () => galleryInputRef.current && galleryInputRef.current.click();

  // Header X close button. We try window.close() first - this page is
  // typically opened in a new tab from the Etendo Mobile Work Order screen, so
  // closing the tab is the most natural exit. If the browser refuses (tab
  // wasn't opened by JS), and we have a returnUrl, navigate there. As a
  // last resort send the user back to /needsaction inside the SPA.
  //
  // closeOnExit=true overrides the returnUrl fallback. We still try
  // window.close(); if the browser refuses, the user can close the tab
  // manually. We deliberately do NOT navigate to returnUrl in this mode
  // because callers like the FlexDoc ManagePictures decorator open the page
  // from a PDF in a fresh tab and bouncing the user to the document's
  // calling page is the wrong exit (TSP-147).
  // Sign In link for anonymous viewers (TSP-121). Routes through Keycloak and
  // returns to this exact Manage Pictures view (same tableId / recordId /
  // returnUrl) so the user lands back where they were, now with full access.
  const handleSignIn = () => {
    const target = window.location.hash.replace(/^#/, "") || "/managepictures";
    keycloakLogin(target);
  };

  const handleClose = () => {
    window.close();
    setTimeout(() => {
      if (!closeOnExit && returnUrl) {
        window.location.href = returnUrl;
        return;
      }
      // Browser refused window.close() (typical for tabs opened from a
      // PDF link or pasted URL rather than via window.open). Give the
      // user device-appropriate guidance for closing the tab.
      const isTouch = (typeof window.matchMedia === "function"
          && window.matchMedia("(pointer: coarse)").matches)
        || ("ontouchstart" in window);
      const closeHint = isTouch
        ? "Use your browser's tab switcher to close this tab."
        : "Press Cmd+W or Ctrl+W to close this tab.";
      message.info(closeHint);
    }, 100);
  };

  // Tap behavior on a tile depends on file type. Images open in an in-page
  // modal (full-size, X-close, backdrop-tap-to-dismiss) so users stay in
  // Manage Pictures. Videos open in a new tab via Etendo's downloadAttachments
  // servlet so the device's native player takes over - the in-page modal
  // can't reliably play arbitrary mobile-recorded codecs.
  const openAttachment = useCallback((it) => {
    if (!it || !it.id) return;
    if (!isVideoName(it.name)) {
      setViewingId(it.id);
      return;
    }
    if (!etendoBaseUrl) return;
    const url = `${etendoBaseUrl.replace(/\/$/, "")}/downloadAttachments?fileId=${encodeURIComponent(it.id)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [etendoBaseUrl]);

  // Header subtitle: render the document identifier (the label / context).
  // When returnUrl is provided we render it as an underlined anchor that
  // navigates back to the calling Etendo page in the same tab. Without
  // returnUrl we render plain text. The label itself comes from the caller
  // (e.g. "Work Order WO-1234"); we don't try to derive it from tableId.
  const docLabel = contextLabel || (canUse ? "Record" : "(no record)");

  return (
    <div
      style={{
        maxWidth: layout.maxWidth,
        margin: "0 auto",
        padding: "0 16px",
        // Anonymous viewers have no sticky bottom bar, so they don't need
        // the tall reserve that keeps the last row above it (TSP-121).
        paddingBottom: readOnly
          ? "calc(24px + env(safe-area-inset-bottom, 0px))"
          : "calc(120px + env(safe-area-inset-bottom, 0px))",
        minHeight: "100vh",
        boxSizing: "border-box",
        background: BRAND.pageBg,
        position: "relative",
      }}
    >
      {/* Header X close - upper-right of the page. Standard mobile pattern;
          replaces the old "Back" link which lived next to the subtitle. */}
      <button
        onClick={handleClose}
        aria-label="Close"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          WebkitTapHighlightColor: "transparent",
          zIndex: 5,
        }}
      >
        <IconClose size={22} color={BRAND.text} />
      </button>

      {/* Sign In - anonymous viewers only (TSP-121). Mirrors the X button in
          the opposite top corner. Replaces the green-header "Sign Out" link,
          which is never rendered for an unauthenticated session. */}
      {readOnly && (
        <button
          onClick={handleSignIn}
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            height: 40,
            padding: "0 12px",
            borderRadius: 8,
            background: "transparent",
            border: `1px solid ${BRAND.primary}`,
            color: BRAND.primary,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
            zIndex: 5,
          }}
        >
          Sign In
        </button>
      )}

      <div style={{ textAlign: "center", padding: "16px 40px 4px" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: BRAND.text }}>Manage Pictures</div>
        <div style={{ fontSize: 14, color: BRAND.textSecondary, marginTop: 4 }}>
          {/* The document back-link is a write-adjacent navigation aid; for
              anonymous viewers (TSP-121) it renders as plain text so they
              cannot deep-link into an authenticated caller page. */}
          {returnUrl && !readOnly ? (
            <a
              href={returnUrl}
              target="_self"
              style={{
                color: BRAND.primary,
                textDecoration: "underline",
                fontWeight: 500,
              }}
            >
              {docLabel}
            </a>
          ) : (
            docLabel
          )}
        </div>
        {canUse && (
          <div style={{ fontSize: 11, color: BRAND.textMuted, marginTop: 2, fontFamily: "monospace" }}>
            {tableId.slice(0, 8)}.../{recordId.slice(0, 8)}...
          </div>
        )}
      </div>

      {!canUse && (
        <div style={{ ...cardStyle, marginTop: 12, color: BRAND.warning }}>
          This page needs <code>tableId</code> and <code>recordId</code> URL params. Launch it from the MWO workflow.
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "4px 4px 8px", gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.text }}>
            {selectMode
              ? `${selectedIds.size} selected`
              : "Existing Pictures"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!selectMode && (
              <div style={{ fontSize: 12, color: BRAND.textMuted }}>
                {loadingExisting ? "Loading..." : `${existing.length} ${existing.length === 1 ? "item" : "items"}`}
              </div>
            )}
            {/* Sort selector. Hidden in select mode (the sticky bottom bar
                already crowds the screen) and when there's nothing to sort. */}
            {!selectMode && !loadingExisting && existing.length > 1 && (
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                aria-label="Sort pictures"
                style={{
                  fontSize: 12,
                  padding: "6px 8px",
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 6,
                  background: "#fff",
                  color: BRAND.text,
                  cursor: "pointer",
                  minHeight: 32,
                }}
              >
                <option value="created-desc">Newest first</option>
                <option value="created-asc">Oldest first</option>
                <option value="description">Description (A-Z)</option>
              </select>
            )}
            {!loadingExisting && existing.length > 0 && !readOnly && (
              <button
                onClick={selectMode ? exitSelectMode : enterSelectMode}
                style={{
                  background: "transparent",
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 6,
                  padding: "6px 12px",
                  minHeight: 40,
                  fontSize: 13,
                  fontWeight: 500,
                  color: selectMode ? BRAND.text : BRAND.primary,
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {selectMode ? "Cancel" : "Select"}
              </button>
            )}
          </div>
        </div>
        {!loadingExisting && (
          <ExistingGrid
            items={sortedExisting}
            columns={layout.columns}
            onOpenAttachment={openAttachment}
            onDelete={deleteExisting}
            onEditDescription={(it) => setEditingItem(it)}
            selectMode={selectMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            readOnly={readOnly}
          />
        )}
      </div>

      {queue.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.text, margin: "4px 4px 8px" }}>
            Uploading ({queue.length})
          </div>
          <div style={cardStyle}>
            {queue.map((q) => (
              <QueueItem key={q.id} item={q} onRemove={removeQueueItem} onChangeDescription={setQueueDescription} />
            ))}
          </div>
        </div>
      )}

      {/* Sticky bottom bar. In select mode it becomes the bulk-edit panel
          (textarea + Apply / Clear). Outside select mode it's the upload
          actions (Take Photo / Gallery). Hidden entirely for anonymous
          read-only viewers (TSP-121) - all of it is write actions. */}
      {!readOnly && (selectMode ? (
        <div
          style={{
            ...cardStyle,
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: BRAND.pageBg,
            borderTop: `1px solid ${BRAND.border}`,
            borderRadius: 0,
            padding: "12px 16px",
            paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
            zIndex: 10,
            maxWidth: layout.maxWidth,
            margin: "0 auto",
            boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <textarea
            value={bulkDraft}
            onChange={(e) => setBulkDraft(e.target.value)}
            maxLength={DESCRIPTION_MAX}
            placeholder={`Description for ${selectedIds.size} selected ${selectedIds.size === 1 ? "picture" : "pictures"}`}
            rows={2}
            style={{
              width: "100%",
              boxSizing: "border-box",
              fontSize: 14,
              padding: "8px 10px",
              border: `1px solid ${BRAND.border}`,
              borderRadius: 6,
              outline: "none",
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
          <div style={{ fontSize: 11, color: BRAND.textMuted, marginTop: 4, textAlign: "right" }}>
            {bulkDraft.length} / {DESCRIPTION_MAX}
          </div>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={applyBulkDescription}
              disabled={bulkSaving || selectedIds.size === 0}
              style={{
                ...btnPrimary,
                width: "100%",
                minHeight: 40,
                opacity: selectedIds.size === 0 ? 0.5 : 1,
              }}
            >
              {bulkSaving ? "Saving..." : `Apply to ${selectedIds.size}`}
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: BRAND.pageBg,
            borderTop: `1px solid ${BRAND.border}`,
            padding: "12px 16px",
            paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
            display: "flex",
            gap: 10,
            zIndex: 10,
            maxWidth: layout.maxWidth,
            margin: "0 auto",
          }}
        >
          <button onClick={handleCamera} style={btnPrimary} disabled={!canUse}>
            <IconCamera /> Take Photo / Video
          </button>
          <button onClick={handleGallery} style={btnSecondary} disabled={!canUse}>
            <IconImages /> Gallery
          </button>
        </div>
      ))}

      {/* Single-item description editor overlay. Renders only when an item
          is being edited; closes on Cancel, on outside-click, or after a
          successful save. */}
      {editingItem && (
        <DescriptionEditor
          item={editingItem}
          onSave={saveDescription}
          onCancel={() => setEditingItem(null)}
        />
      )}

      {/* Full-size image viewer. Only images use this; videos open in a
          new tab from openAttachment. Carries hasPrev/hasNext so the user
          can flip through images in the current sort order without going
          back to the grid. */}
      {viewingItem && (
        <ImageViewer
          item={viewingItem}
          hasPrev={viewingIndex > 0}
          hasNext={viewingIndex >= 0 && viewingIndex < viewableItems.length - 1}
          onPrev={showPrev}
          onNext={showNext}
          onClose={() => setViewingId(null)}
        />
      )}

      {/* Both inputs accept images and videos. We deliberately omit
          `capture` so iOS shows the full picker (which lets the user choose
          "Take Photo or Video"). The Gallery input keeps `multiple`. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,video/*"
        style={{ display: "none" }}
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}
